import React from "react";
import { styled } from "@linaria/react";
import { color } from "..";
import type { DateFieldState, DateSegment as DateSegmentType } from "react-stately";
import { useDateSegment } from "react-aria";
import Text from "../Text";
import classNames from "classnames";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";

const DateSegmentWrapper = styled.div`
  border-radius: 4px;
  padding: 2px 0;

  &:focus-within {
    background-color: ${color.lightNitrogen50};
    color: ${color.carbon900};
  }

  .placeholder {
    color: ${color.carbon500};
  }

  span {
    &:focus-visible {
      outline: none;
    }
  }
`;

interface Props {
  segment: DateSegmentType;
  state: DateFieldState;
  forwardedRef?: ((el: HTMLSpanElement | null) => void) | React.RefObject<HTMLSpanElement>;
  onFocus?: () => void;
}

function DateSegment({ segment, state, forwardedRef, onFocus }: Props) {
  const ref = useEnsuredForwardedRef(forwardedRef || null);

  const { segmentProps } = useDateSegment(segment, state, ref);
  const text = segment.text.toUpperCase();

  if (segment.type === "literal") {
    return (
      <Text
        type="body1"
        {...segmentProps}
        onFocus={onFocus}
        className={classNames({
          placeholder: segment.isPlaceholder,
        })}
      >
        <span ref={ref}>{text}</span>
      </Text>
    );
  }

  return (
    <DateSegmentWrapper>
      <Text type="body1">
        <span
          {...segmentProps}
          tabIndex={segmentProps.tabIndex ?? 0}
          onFocus={onFocus}
          ref={ref}
          className={classNames({
            placeholder: segment.isPlaceholder,
            disabled: state.isDisabled,
          })}
        >
          {text}
        </span>
      </Text>
    </DateSegmentWrapper>
  );
}

export default DateSegment;
